import { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, Mail, MousePointer, Eye, XCircle, Download } from 'lucide-react';
import { adminAxios } from './AdminLayout';

var BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAnalytics() {
  var [data, setData] = useState(null);
  var [period, setPeriod] = useState(null);
  var [loading, setLoading] = useState(true);

  function fetchData(days) {
    setLoading(true);
    var url = '/api/admin/analytics/dashboard';
    if (days) url += '?days=' + days;
    adminAxios().get(url).then(function(res) { setData(res.data); setLoading(false); }).catch(function() { setLoading(false); });
  }

  useEffect(function() { fetchData(period); }, [period]);

  function handleExport() {
    var token = localStorage.getItem('admin_token');
    var url = BACKEND_URL + '/api/admin/analytics/export' + (period ? '?days=' + period : '');
    fetch(url, { headers: { Authorization: 'Bearer ' + token } }).then(function(r) { return r.blob(); }).then(function(blob) {
      var a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'analytics.csv'; a.click();
    });
  }

  if (loading) return <div className="animate-pulse font-manrope text-slate-500">Loading analytics...</div>;
  if (!data) return <div className="font-manrope text-slate-500">No analytics data available yet.</div>;

  var t = data.totals || {};
  var cards = [
    { label: 'Sent', value: t.sent, icon: Mail, color: 'bg-blue-500' },
    { label: 'Delivered', value: t.delivered, icon: Mail, color: 'bg-cyan-500' },
    { label: 'Opened', value: t.opened, icon: Eye, color: 'bg-green-500' },
    { label: 'Clicked', value: t.clicked, icon: MousePointer, color: 'bg-purple-500' },
    { label: 'Replied', value: t.replied, icon: TrendingUp, color: 'bg-emerald-500' },
    { label: 'Bounced', value: t.bounced, icon: XCircle, color: 'bg-red-400' },
    { label: 'Unsubscribed', value: t.unsubscribed, icon: XCircle, color: 'bg-orange-400' },
  ];

  return (
    <div data-testid="admin-analytics-page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-outfit text-2xl font-bold text-slate-900">Analytics</h2>
        <div className="flex items-center gap-2">
          {[{d: 7, l: '7 days'}, {d: 30, l: '30 days'}, {d: 90, l: '90 days'}, {d: null, l: 'All time'}].map(function(p) {
            return <button key={String(p.d)} onClick={function(){setPeriod(p.d);}} className={'px-3 py-1.5 rounded-full font-manrope text-xs font-medium ' + (period === p.d ? 'bg-[#0096D6] text-white' : 'bg-slate-100 text-slate-600')}>{p.l}</button>;
          })}
          <button onClick={handleExport} className="flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 font-manrope text-xs text-slate-600"><Download className="w-3 h-3" /> Export</button>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {cards.map(function(c, i) {
          return <div key={i} className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm text-center">
            <div className={'w-8 h-8 rounded-lg mx-auto mb-2 flex items-center justify-center ' + c.color}><c.icon className="w-4 h-4 text-white" /></div>
            <p className="font-space text-xl font-bold text-slate-900">{c.value || 0}</p>
            <p className="font-manrope text-[10px] text-slate-500">{c.label}</p>
          </div>;
        })}
      </div>

      {/* Rates */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <p className="font-manrope text-sm text-slate-500 mb-1">Open Rate</p>
          <p className="font-space text-3xl font-bold text-green-600">{data.open_rate || 0}%</p>
          <div className="w-full bg-slate-100 rounded-full h-2 mt-2"><div className="bg-green-500 rounded-full h-2" style={{width: Math.min(data.open_rate || 0, 100) + '%'}}></div></div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <p className="font-manrope text-sm text-slate-500 mb-1">Click Rate</p>
          <p className="font-space text-3xl font-bold text-purple-600">{data.click_rate || 0}%</p>
          <div className="w-full bg-slate-100 rounded-full h-2 mt-2"><div className="bg-purple-500 rounded-full h-2" style={{width: Math.min(data.click_rate || 0, 100) + '%'}}></div></div>
        </div>
      </div>

      {/* Top Offers */}
      {(data.top_offers || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-100 p-6 shadow-sm">
          <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">Top Performing Offers</h3>
          <div className="space-y-2">
            {data.top_offers.map(function(o, i) {
              return <div key={i} className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-xl">
                <span className="font-manrope text-sm text-slate-900">{o.subject}</span>
                <span className="font-space text-sm font-bold text-green-600">{o.opens} opens</span>
              </div>;
            })}
          </div>
        </div>
      )}
    </div>
  );
}
